/**
 * Features:
 * - PortalIllustration base for the §14.4.5 house line-art set: single-colour ink
 *   strokes on `var(--hp-ink)`, accent details on `var(--hp-accent)`, no fills.
 *   Always semantic — `role="img"` with a localisable `title` (required).
 */
import type { ReactNode, SVGProps } from 'react';

export interface PortalIllustrationProps extends Omit<
  SVGProps<SVGSVGElement>,
  'children' | 'title'
> {
  /** Localised accessible name — required, illustrations are never decorative. */
  title: string;
  /** Rendered width in px; height follows the viewBox aspect. */
  width?: number;
  viewBox: string;
  children?: ReactNode;
}

export const ILLUSTRATION_INK = 'var(--hp-ink, #1b1714)';
export const ILLUSTRATION_ACCENT = 'var(--hp-accent, #7a2e22)';

export function PortalIllustration({
  title,
  width = 160,
  viewBox,
  children,
  ...rest
}: PortalIllustrationProps) {
  const [, , w, h] = viewBox.split(' ').map(Number);
  return (
    <svg
      width={width}
      height={(width * (h || 1)) / (w || 1)}
      viewBox={viewBox}
      fill="none"
      stroke={ILLUSTRATION_INK}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      {...rest}
    >
      <title>{title}</title>
      {children}
    </svg>
  );
}
